import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import TransactionLogger from '../utils/transactionLogger';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  async function signup(email, password, userData) {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Create user profile in Firestore
    await setDoc(doc(db, 'users', user.uid), {
      email: user.email,
      ...userData,
      createdAt: new Date()
    });
    
    return user;
  }

  async function login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Log the login event
      await TransactionLogger.logTransaction(
        'LOGIN',
        'USER',
        user.uid,
        null,
        { email: user.email, loginTime: new Date() },
        user.uid,
        user.email
      );
      
      return userCredential;
    } catch (error) {
      // Log failed login attempt
      await TransactionLogger.logTransaction(
        'LOGIN_FAILED',
        'USER',
        'unknown',
        null,
        { email, error: error.message, loginTime: new Date() },
        'unknown',
        email
      );
      throw error;
    }
  }

  async function logout() {
    try {
      // Log the logout event before signing out
      if (currentUser) {
        await TransactionLogger.logTransaction(
          'LOGOUT',
          'USER',
          currentUser.uid,
          null,
          { email: currentUser.email, logoutTime: new Date() },
          currentUser.uid,
          currentUser.email
        );
      }
      
      return signOut(auth);
    } catch (error) {
      console.error('Error logging logout:', error);
      // Still proceed with logout even if logging fails
      return signOut(auth);
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user);
      if (user) {
        setCurrentUser(user);
        console.log('Fetching user profile for:', user.uid);
        // Fetch user profile
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const profileData = userDoc.data();
            console.log('User profile loaded:', profileData);
            setUserProfile(profileData);
            setLoading(false);
          } else {
            console.log('User profile document does not exist - creating basic profile');
            // Create a basic user profile if one doesn't exist
            // Try to extract name from Firebase Auth user object
            const displayName = user.displayName || '';
            const nameParts = displayName.split(' ');
            const firstName = nameParts[0] || user.email?.split('@')[0] || 'User';
            const lastName = nameParts.slice(1).join(' ') || 'Name';
            
            const basicProfile = {
              email: user.email,
              firstName: firstName,
              lastName: lastName,
              organizationName: 'Default Organization',
              role: 'admin',
              createdAt: new Date()
            };
            
            try {
              await setDoc(doc(db, 'users', user.uid), basicProfile);
              console.log('Basic user profile created:', basicProfile);
              setUserProfile(basicProfile);
            } catch (createError) {
              console.error('Error creating basic user profile:', createError);
              setUserProfile(null);
            }
            setLoading(false);
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          setUserProfile(null);
          setLoading(false);
        }
      } else {
        setCurrentUser(null);
        setUserProfile(null);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    setUserProfile,
    loading,
    signup,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

